import React, { useState, useEffect } from "react";
import Modal from "../Modal/Modal";
import { Toggle } from "../Toggle/Toggle";
import { Button } from "../Button";
import { useTheme } from "../../contexts/Theme/ThemeContext";
import Swal from "sweetalert2";
import {
  LayoutImpressaoPedido,
  LayoutImpressaoSacola,
  LayoutTabelaProdutos,
  defaultLayoutPedido,
  defaultLayoutSacola,
  labelsCamposPedido,
  labelsCamposSacola,
  labelsCamposTabelaProdutos,
} from "../../types/LayoutImpressao";
import { ModalLayoutImpressaoContainer } from "./styles";

interface ModalLayoutImpressaoProps {
  isOpen: boolean;
  onClose: () => void;
  tipo: "pedido" | "sacola";
}

const ModalLayoutImpressao: React.FC<ModalLayoutImpressaoProps> = ({
  isOpen,
  onClose,
  tipo,
}) => {
  const { theme } = useTheme();
  const localStorageKey = tipo === "pedido" ? "layoutImpressaoPedidos" : "layoutImpressaoSacolas";

  const getDefaultConfig = () => {
    return tipo === "pedido" ? { ...defaultLayoutPedido } : { ...defaultLayoutSacola };
  };

  const getStoredConfig = (): LayoutImpressaoPedido | LayoutImpressaoSacola => {
    const stored = localStorage.getItem(localStorageKey);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return getDefaultConfig();
      }
    }
    return getDefaultConfig();
  };

  const [config, setConfig] = useState<LayoutImpressaoPedido | LayoutImpressaoSacola>(getStoredConfig());

  useEffect(() => {
    if (isOpen) {
      setConfig(getStoredConfig());
    }
  }, [isOpen]);

  const handleToggleField = (field: string, value: boolean) => {
    setConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleToggleTabelaField = (field: keyof LayoutTabelaProdutos, value: boolean) => {
    setConfig((prev) => ({
      ...prev,
      tabelaProdutos: {
        ...prev.tabelaProdutos,
        [field]: value,
      },
    }));
  };

  const handleSave = () => {
    // Verificar se pelo menos um campo da tabela de produtos está ativo (excluindo footer)
    const camposTabelaAtivos = Object.entries(config.tabelaProdutos)
      .filter(([key]) => !key.startsWith("footer"))
      .some(([, value]) => value === true);

    if (!camposTabelaAtivos) {
      Swal.fire({
        icon: "warning",
        title: "Atenção",
        text: "Selecione pelo menos um campo da tabela de produtos.",
      });
      return;
    }

    localStorage.setItem(localStorageKey, JSON.stringify(config));
    onClose();
  };

  const handleCancel = () => {
    setConfig(getStoredConfig());
    onClose();
  };

  const handleReset = () => {
    setConfig(getDefaultConfig());
  };

  const labels = tipo === "pedido" ? labelsCamposPedido : labelsCamposSacola;
  const camposGerais = Object.keys(labels) as Array<keyof typeof labels>;

  // Separar campos da tabela de produtos e do rodapé
  const camposTabelaProduto = Object.keys(labelsCamposTabelaProdutos).filter(
    (key) => !key.startsWith("footer")
  ) as Array<keyof LayoutTabelaProdutos>;

  const camposFooter = Object.keys(labelsCamposTabelaProdutos).filter(
    (key) => key.startsWith("footer")
  ) as Array<keyof LayoutTabelaProdutos>;

  return (
    <Modal
      isOpen={isOpen}
      setModal={onClose}
      width="lg"
      maxWidth="600px"
      align="center"
      justify="center"
      padding="24px"
    >
      <ModalLayoutImpressaoContainer theme={theme}>
        <div className="header">
          <h2 className="fonteTitulo">Configurar layout de impressão</h2>
          <p className="fonteTexto subtitulo">
            Selecione quais campos deseja exibir na impressão
          </p>
        </div>

        <div className="content scrollBonito">
          <div className="secao">
            <h3 className="fonteSubtitulo secao-titulo">
              {tipo === "pedido" ? "Informações do pedido" : "Informações da sacola"}
            </h3>
            <div className="campos-grid">
              {camposGerais.map((campo) => (
                <label key={campo} className="campo fonteTexto">
                  <Toggle
                    value={config[campo] as boolean}
                    onChange={(value) => handleToggleField(campo, value)}
                  />
                  <span>{labels[campo]}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="secao">
            <h3 className="fonteSubtitulo secao-titulo">Tabela de produtos</h3>
            <div className="campos-grid">
              {camposTabelaProduto.map((campo) => (
                <label key={campo} className="campo fonteTexto">
                  <Toggle
                    value={config.tabelaProdutos[campo]}
                    onChange={(value) => handleToggleTabelaField(campo, value)}
                  />
                  <span>{labelsCamposTabelaProdutos[campo]}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="secao">
            <h3 className="fonteSubtitulo secao-titulo">Rodapé da tabela</h3>
            <div className="campos-grid">
              {camposFooter.map((campo) => (
                <label key={campo} className="campo fonteTexto">
                  <Toggle
                    value={config.tabelaProdutos[campo]}
                    onChange={(value) => handleToggleTabelaField(campo, value)}
                  />
                  <span>{labelsCamposTabelaProdutos[campo]}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="footer">
          <Button
            title="Restaurar padrão"
            className="botao-reset"
            onClick={handleReset}
          />
          <div className="botoes-principais">
            <Button
              title="Cancelar"
              className="botao-cancelar"
              onClick={handleCancel}
            />
            <Button
              title="Salvar"
              className="botao-salvar"
              onClick={handleSave}
            />
          </div>
        </div>
      </ModalLayoutImpressaoContainer>
    </Modal>
  );
};

export default ModalLayoutImpressao;
